import type { NavigateFunction } from 'react-router-dom';
import { toast } from 'sonner';
import i18n from 'i18next';
import { AppRoutes } from '@/app/routes';
import { useInterfaceStore } from '@/store/useInterfaceStore';

/** Back from these screens leaves the app, as on any Android app's first screen. */
const ROOT_PATHS: ReadonlySet<string> = new Set([AppRoutes.dashboard.home, AppRoutes.auth.login]);

const OPEN_DIALOG = ['[aria-modal="true"]', '[role="dialog"]', '[role="alertdialog"]']
  .map((selector) => `${selector}:not([aria-hidden="true"])`)
  .join(', ');

/** Time for a dialog to close on Escape before Back moves on. */
export const DIALOG_CLOSE_WAIT_MS = 150;

const LIVE_EXIT_HINT_MS = 2500;

export interface NativeBackContext {
  pathname: string;
  navigate: NavigateFunction;
  exit: () => Promise<void>;
}

/**
 * A screen's own Back, asked before the default one. Returns true when it handled
 * Back (previous step of a wizard, exit confirmation), false to let it go on.
 * `leave` runs the default, for a handler that leaves once the user has confirmed.
 */
export type NativeBackHandler = (leave: () => void) => boolean;

const screenHandlers: NativeBackHandler[] = [];

/** The last handler pushed is asked first. Returns the function that removes it. */
export function pushNativeBackHandler(handler: NativeBackHandler): () => void {
  screenHandlers.push(handler);
  return () => {
    const index = screenHandlers.lastIndexOf(handler);
    if (index >= 0) screenHandlers.splice(index, 1);
  };
}

function topDialog(): Element | undefined {
  const dialogs = document.querySelectorAll(OPEN_DIALOG);
  return dialogs[dialogs.length - 1];
}

function goBack(context: NativeBackContext): void {
  const leave = () => leaveScreen(context);
  for (let i = screenHandlers.length - 1; i >= 0; i -= 1) {
    if (screenHandlers[i](leave)) return;
  }
  leave();
}

function leaveScreen({ pathname, navigate, exit }: NativeBackContext): void {
  // Live entry is never left by a stray Back between two points: only its own exit
  // button leaves it, after warning about actions not yet synced. Back says so,
  // otherwise it looks broken.
  if (pathname === AppRoutes.match.input) {
    // At the bottom: the default top position covers the scoreboard header and the very
    // ← it points to.
    toast.info(i18n.t('common:native_back.live_exit_hint'), {
      id: 'native-back-live',
      position: 'bottom-center',
      duration: LIVE_EXIT_HINT_MS,
    });
    return;
  }

  if (ROOT_PATHS.has(pathname)) {
    void exit();
    return;
  }

  // React Router keeps the history index: 0 means the app was opened on this screen (deep link).
  const historyIndex = (window.history.state as { idx?: number } | null)?.idx ?? 0;
  if (historyIndex > 0) navigate(-1);
  else navigate(AppRoutes.dashboard.home, { replace: true });
}

/**
 * Android Back, in the order users expect: close what is open on top of the
 * screen, then the screen's own Back (a wizard's previous step), then go back a
 * screen, and leave the app from its first screen.
 */
export function handleNativeBack(context: NativeBackContext): void {
  const { sidebar, setSidebarOpen } = useInterfaceStore.getState();
  if (sidebar.isOpen && !sidebar.isDesktop) {
    setSidebarOpen(false);
    return;
  }

  const dialog = topDialog();
  if (!dialog) {
    goBack(context);
    return;
  }

  // Most dialogs close on Escape. Some do not (guided tour, end-of-point prompts): Back
  // must not get stuck on them, so it moves on once the dialog has had time to close.
  dialog.dispatchEvent(new KeyboardEvent('keydown', { key: 'Escape', bubbles: true, cancelable: true }));
  window.setTimeout(() => {
    if (dialog.isConnected && dialog.matches(OPEN_DIALOG)) goBack(context);
  }, DIALOG_CLOSE_WAIT_MS);
}
